import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';

// Unlike stock shadcn, this app deliberately lets `rows` own the minimum height. The text is 16px under a
// finger, as in `Input` (iOS zooms into a focused field set below 16px), and 14px under a mouse.
//
// The `variant` axis is `Input`'s, for the same reason it has one: a textarea that IS the surface — the chat
// composer and the inline message editor, where the bordered box around it is the field — states `line`
// instead of hand-rolling a transparent field of its own. `line` drops the box, not the typography or the
// touch floor, so a composer keeps the app's field text and only its frame moves to its parent.
const textareaVariants = cva(
  [
    'w-full rounded-md border border-input bg-control px-3 py-2 text-ui pointer-coarse:text-body text-foreground',
    'placeholder:text-muted-foreground transition-[border-color,background-color,box-shadow]',
    'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
    'disabled:cursor-not-allowed disabled:opacity-40',
    'aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20',
  ],
  {
    variants: {
      variant: {
        default: '',
        line: 'rounded-none border-0 bg-transparent focus-visible:ring-0',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & VariantProps<typeof textareaVariants>>(
  function Textarea({ className, variant, ...props }, ref) {
    return <textarea ref={ref} data-slot="textarea" className={cn(textareaVariants({ variant }), className)} {...props} />;
  },
);
